import React, { useState, useEffect } from "react";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import {
  FaFileAlt,
  FaUpload,
  FaFilePdf,
  FaTrash,
  FaEye,
} from "react-icons/fa";
import Swal from "sweetalert2";
import FileUploadWithScanner from "../../components/common/FileUploadWithScanner";

const CoachWeeklyReports = () => {
  const [athletes, setAthletes] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [athleteId, setAthleteId] = useState("");
  const [weekNumber, setWeekNumber] = useState(1);
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [pdfFile, setPdfFile] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [athletesRes, reportsRes] = await Promise.all([
        API.camp.coachPortal.getMyAthletes(),
        API.camp.coachPortal.getWeeklyReports(),
      ]);

      const athletesList = athletesRes?.data?.athletes || athletesRes?.athletes || [];
      setAthletes(athletesList);
      if (athletesList.length > 0 && !athleteId) {
        setAthleteId(athletesList[0].id);
      }

      setReports(reportsRes?.data?.reports || reportsRes?.reports || []);
    } catch (err) {
      console.error("Failed to load weekly reports data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!athleteId) {
      Swal.fire("Error", "Please select an athlete", "warning");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("athlete_id", athleteId);
    formData.append("week_number", weekNumber);
    formData.append("report_date", reportDate);
    formData.append("description", description);
    if (pdfFile) {
      formData.append("pdf_file", pdfFile);
    }

    try {
      const activeCampRes = await API.camp.coachPortal.getActiveCamp();
      const campId = activeCampRes?.data?.camp?.id || activeCampRes?.camp?.id || 1;
      formData.append("camp_id", campId);

      const res = await API.camp.coachPortal.submitWeeklyReport(formData);
      if (res?.data?.success || res?.success) {
        Swal.fire("Success", `Week ${weekNumber} report submitted successfully!`, "success");
        setDescription("");
        setPdfFile(null);
        // Refresh reports list
        const reportsRes = await API.camp.coachPortal.getWeeklyReports();
        setReports(reportsRes?.data?.reports || reportsRes?.reports || []);
      }
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to submit weekly report", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Weekly Report?",
      text: "This report and attached file will be permanently removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, Delete",
    });

    if (confirm.isConfirmed) {
      try {
        await API.camp.coachPortal.deleteWeeklyReport(id);
        Swal.fire("Deleted", "Report deleted successfully.", "success");
        setReports((prev) => prev.filter((r) => r.id !== id));
      } catch (err) {
        Swal.fire("Error", "Failed to delete report", "error");
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <section className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Coaching Documentation</p>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-0.5 tracking-tight flex items-center gap-2">
            <FaFileAlt className="text-cyan-700" /> Weekly Coaching Reports
          </h1>
          <p className="text-xs text-slate-600 mt-1">
            Submit and manage Weeks 1 to 9 athlete coaching performance reports with PDF attachments
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Submit Report Form */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2 pb-3 border-b border-slate-200">
            <FaUpload className="text-cyan-700" /> Submit Weekly Report
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4 text-xs">
            <div>
              <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider">Athlete</label>
              <select
                value={athleteId}
                onChange={(e) => setAthleteId(e.target.value)}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2.5 text-slate-900 font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
                required
              >
                {athletes.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} (BIB: {a.bib_no})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider">Week Number</label>
                <select
                  value={weekNumber}
                  onChange={(e) => setWeekNumber(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((w) => (
                    <option key={w} value={w}>
                      Week {w}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider">Report Date</label>
                <input
                  type="date"
                  value={reportDate}
                  onChange={(e) => setReportDate(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider">
                Progress Description & Recommendations
              </label>
              <textarea
                rows={4}
                placeholder="Weekly drills, training milestones, athlete physical condition, technical adaptations..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                required
              />
            </div>

            <div>
              <FileUploadWithScanner
                label="Attach Report PDF / Document (Optional)"
                name="pdfFile"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                value={pdfFile}
                onChange={(file) => setPdfFile(file)}
                scannerTitle="Scan Weekly Report"
                targetFileName={`weekly_report_w${weekNumber}_${Date.now()}.jpg`}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-cyan-700 hover:bg-cyan-800 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 transition-all uppercase tracking-wider"
            >
              {submitting ? "Uploading..." : `Submit Week ${weekNumber} Report`}
            </button>
          </form>
        </div>

        {/* Submitted Reports Archive */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-200">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <FaFilePdf className="text-amber-600" /> Submitted Weekly Reports Archive ({reports.length})
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl">
              <FaFileAlt className="text-slate-400 text-4xl mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-700">No reports submitted yet</p>
              <p className="text-xs text-slate-500 mt-1">
                Submitted reports with PDF attachments will appear in this historical archive.
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="p-4 bg-slate-50 border border-slate-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-cyan-300 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-50 border border-cyan-200 text-cyan-800 flex items-center justify-center font-bold text-xs shrink-0">
                      W{report.week_number}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold text-slate-900">
                          {report.athlete?.name || "Athlete"}
                        </h4>
                        <span className="text-[10px] bg-white text-slate-700 px-2 py-0.5 rounded border border-slate-200 font-semibold">
                          BIB: {report.athlete?.bib_no}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                        {report.description}
                      </p>
                      <span className="text-[10px] text-slate-400 mt-1 block">
                        Date: {report.report_date} &bull; Coach: {report.coach?.name || "Self"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-end sm:self-center">
                    {report.file_path && (
                      <a
                        href={resolveAssetUrl(report.file_path)}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1.5 bg-cyan-700 text-white hover:bg-cyan-800 rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm"
                      >
                        <FaEye /> PDF
                      </a>
                    )}
                    {report.is_locked ? (
                      <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg font-bold flex items-center gap-1 shadow-sm">
                        🔒 Locked
                      </span>
                    ) : (
                      <button
                        onClick={() => handleDelete(report.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Report"
                      >
                        <FaTrash className="text-xs" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachWeeklyReports;
